import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Control, useFormContext, useWatch } from 'react-hook-form';
import { AgeSource } from '@/api/enum/common.enum';
import { API_DATE_FORMAT } from '@/utils/dateHelper';
import { isEstimatedAgeSource } from '@/utils/ageHelper';
import AppRadioGroup from './AppRadioGroup';
import AppDatePicker from './AppDatePicker';
import AppTextField, { InputType } from './AppTextField';

interface Props {
	control: Control<any>;
	/** Labels in Chinese (出生日期／估計年齡) for the Chinese forms. */
	lang?: 'en' | 'zh';
	/** Label above the radio buttons, e.g. "Age information*". */
	label?: string;
	dateOfBirthRequired?: boolean;
	estimatedAgeRequired?: boolean;
	/** Grid size of the date of birth / estimated age input. */
	inputSize?: number;
}

/**
 * Date of birth / Estimated age radio buttons, and the input of the chosen
 * source (CHA-009). Renders two Grid items: the radio row on its own line, then
 * the input. Must be inside a `FormProvider` and a Grid container.
 */
export default function AppAgeInput({
	control,
	lang = 'en',
	label,
	dateOfBirthRequired = true,
	estimatedAgeRequired = true,
	inputSize = 6,
}: Props) {
	const { t } = useTranslation();
	const { clearErrors } = useFormContext();
	const ageSource = useWatch({ control, name: 'ageSource' });
	const estimated = isEstimatedAgeSource(ageSource);

	const dateOfBirthLabel =
		lang === 'zh' ? t('memberAge.dateOfBirthZh') : t('memberAge.dateOfBirth');
	const estimatedAgeLabel =
		lang === 'zh' ? t('memberAge.estimatedAgeZh') : t('memberAge.estimatedAge');

	return (
		<>
			<Grid size={12}>
				<AppRadioGroup
					control={control}
					name='ageSource'
					label={label}
					row
					style={{ padding: 0 }}
					options={[
						{ id: AgeSource.dateOfBirth, name: dateOfBirthLabel },
						{ id: AgeSource.estimatedAge, name: estimatedAgeLabel },
					]}
					handleChange={() => clearErrors(['dateOfBirth', 'estimatedAge'])}
				/>
			</Grid>
			<Grid size={inputSize}>
				{estimated ? (
					<AppTextField
						key='estimatedAge'
						control={control}
						name='estimatedAge'
						inputType={InputType.number}
						maxLength={3}
						label={estimatedAgeLabel + (estimatedAgeRequired ? '*' : '')}
					/>
				) : (
					<AppDatePicker
						key='dateOfBirth'
						maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
						control={control}
						name='dateOfBirth'
						label={dateOfBirthLabel + (dateOfBirthRequired ? '*' : '')}
					/>
				)}
			</Grid>
		</>
	);
}
